import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ChatBubbleLeftIcon, 
  CpuChipIcon,
  CloudArrowUpIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';

const DashboardOverview = ({ user }) => {
  const [stats, setStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemHealth, setSystemHealth] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      await Promise.all([
        loadMediaStats(),
        loadRecentActivity(),
        loadSystemHealth()
      ]);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMediaStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/media/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load media stats:', error);
    }
  };

  const loadRecentActivity = async () => {
    // Mock recent activity - in real implementation, this would come from API
    setRecentActivity([
      {
        id: 1,
        type: 'chat',
        description: 'Started new chat session',
        timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        icon: ChatBubbleLeftIcon,
        color: 'text-blue-500'
      },
      {
        id: 2,
        type: 'upload',
        description: 'Uploaded vacation photos (12 images)',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        icon: PhotoIcon,
        color: 'text-green-500'
      },
      {
        id: 3,
        type: 'agent',
        description: 'Used Weather Agent',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
        icon: CpuChipIcon,
        color: 'text-purple-500'
      },
      {
        id: 4,
        type: 'memory',
        description: 'Searched for "family vacation"',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
        icon: MagnifyingGlassIcon,
        color: 'text-pink-500'
      }
    ]);
  };

  const loadSystemHealth = async () => {
    // Mock system health - in real implementation, this would come from API
    setSystemHealth({
      ai_service: 'online',
      vector_store: 'online',
      media_processing: 'online',
      agents: 'online',
      uptime: '24h 31m',
      last_backup: '2 hours ago'
    });
  };

  const quickActions = [
    {
      name: 'Start Chat',
      description: 'Begin a new AI conversation',
      icon: ChatBubbleLeftIcon,
      href: '/chat',
      enabled: user?.can_chat,
      color: 'bg-blue-500',
      shortcut: 'C'
    },
    {
      name: 'Upload Media',
      description: 'Process images, videos, or documents',
      icon: CloudArrowUpIcon,
      href: '/upload',
      enabled: user?.can_upload,
      color: 'bg-green-500',
      shortcut: 'U'
    },
    {
      name: 'Search Memory',
      description: 'Find content across all media',
      icon: MagnifyingGlassIcon,
      href: '/memory',
      enabled: user?.can_upload,
      color: 'bg-pink-500',
      shortcut: 'S'
    },
    {
      name: 'Use Agents',
      description: 'Access specialized AI agents',
      icon: CpuChipIcon,
      href: '/agents',
      enabled: user?.can_use_agents,
      color: 'bg-purple-500',
      shortcut: 'A'
    }
  ];

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'online': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'warning': return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      default: return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">
          Welcome to Jarvis AI Phase 5! 🚀
        </h2>
        <p className="text-blue-100 mb-4">
          Enhanced dashboard with comprehensive controls, analytics, and memory management.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
            🔥 New: Tabbed Dashboard
          </span>
          <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
            📊 Enhanced Analytics
          </span>
          <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
            🧠 Memory Insights
          </span>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ChatBubbleLeftIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Chat Sessions</p>
              <p className="text-2xl font-bold text-gray-900">--</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <PhotoIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Media Items</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_items || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CpuChipIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Agent Calls</p>
              <p className="text-2xl font-bold text-gray-900">--</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-pink-100 rounded-lg">
              <ArrowTrendingUpIcon className="w-6 h-6 text-pink-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Efficiency</p>
              <p className="text-2xl font-bold text-gray-900">98%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <div key={action.name} className="relative">
              {action.enabled ? (
                <Link
                  to={action.href}
                  className="block p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-md transition-all duration-200"
                >
                  <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center mb-3`}>
                    <action.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-medium text-gray-900">{action.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                  <div className="absolute top-2 right-2">
                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                      {action.shortcut}
                    </span>
                  </div>
                </Link>
              ) : (
                <div className="block p-4 border border-gray-200 rounded-lg bg-gray-50 opacity-50">
                  <div className={`w-10 h-10 bg-gray-300 rounded-lg flex items-center justify-center mb-3`}>
                    <action.icon className="w-6 h-6 text-gray-500" />
                  </div>
                  <h4 className="font-medium text-gray-600">{action.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">Access restricted</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <activity.icon className={`w-5 h-5 ${activity.color}`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <p className="text-xs text-gray-500 flex items-center mt-1">
                    <ClockIcon className="w-3 h-3 mr-1" />
                    {formatTimeAgo(activity.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Link
              to="/analytics"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View all activity →
            </Link>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
          <div className="space-y-3">
            {Object.entries(systemHealth).map(([key, value]) => {
              if (key === 'uptime' || key === 'last_backup') {
                return (
                  <div key={key} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 capitalize">
                      {key.replace('_', ' ')}:
                    </span>
                    <span className="text-sm font-medium text-gray-900">{value}</span>
                  </div>
                );
              }
              return (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 capitalize">
                    {key.replace('_', ' ')}:
                  </span>
                  <div className="flex items-center space-x-2">
                    {getHealthIcon(value)}
                    <span className="text-sm font-medium text-gray-900 capitalize">{value}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Link
              to="/admin"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View system details →
            </Link>
          </div>
        </div>
      </div>

      {/* Phase 5 Features */}
      <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-6 border border-pink-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">🎯 Phase 5 Features</h3>
        <p className="text-gray-700 mb-4">
          Your dashboard now includes advanced controls and analytics for comprehensive system management.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
            <span>Tabbed Navigation</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
            <span>Memory Management</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
            <span>Usage Analytics</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
            <span>Permission Controls</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
            <span>System Health Monitor</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
            <span>Enhanced Settings</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;